import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuth } from "firebase/auth";
import { collection, getDoc, getDocs, doc } from 'firebase/firestore';
import { db } from '../firebase';

const UploadArtScreen = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [artistName, setArtistName] = useState('');
  const [artistBirthYear, setArtistBirthYear] = useState('');
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    createdYear: '',
    categoryIds: [],
    isPublicArt: false,
    isForSale: true,
    price: '',
    materials: '',
    size: '',
    description: '',
    allowBuyerPickUp: false,
    zipCode: '',
    image: null,
  });

  useEffect(() => {
    // Handle edit mode data from location state
    if (location.state) {
      const { editMode, artworkData, artistName: stateArtistName, artistBirthYear: stateArtistBirthYear, currentUserId: stateCurrentUserId } = location.state;
      
      if (editMode && artworkData) {
        setIsEditMode(true);
        setFormData({
          title: artworkData.title || '',
          createdYear: artworkData.createdYear || '',
          categoryIds: artworkData.categoryIds || [],
          isPublicArt: artworkData.isPublicArt || false,
          isForSale: artworkData.isForSale || false,
          price: artworkData.price || '',
          materials: artworkData.materials || '',
          size: artworkData.size || '',
          description: artworkData.description || '',
          allowBuyerPickUp: artworkData.allowBuyerPickUp || false,
          zipCode: artworkData.zipCode || '',
          image: artworkData.image || null,
        });
        
        if (stateArtistName) setArtistName(stateArtistName);
        if (stateArtistBirthYear) setArtistBirthYear(stateArtistBirthYear);
        if (stateCurrentUserId) setCurrentUserId(stateCurrentUserId);
      }
    }
  }, [location.state]);

  useEffect(() => {
    fetchCategories();
    fetchCurrentUserInfo();
  }, []);

  const fetchCategories = async () => {
    try {
      const categoriesCollection = collection(db, 'art_categories');
      const categoriesSnapshot = await getDocs(categoriesCollection);
      const categoriesList = categoriesSnapshot.docs.map(doc => ({
        id: doc.id,
        name: formatCategoryName(doc.data().name),
        ref: doc.ref
      }));
      setCategories(categoriesList);
    } catch (error) {
      console.error("Error fetching categories: ", error);
    }
  };

  const fetchCurrentUserInfo = async () => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (user) {
      setCurrentUserId(user.uid);
      try {
        const userDocRef = doc(db, 'users', user.uid);
        const userDocSnap = await getDoc(userDocRef);

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          setArtistName(userData.displayName || 'Unknown Artist');
          setArtistBirthYear(userData.birthYear || 'Unknown');
        } else {
          console.log("No user document found!");
          setArtistName('Unknown Artist');
          setArtistBirthYear('Unknown');
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        setArtistName('Unknown Artist');
        setArtistBirthYear('Unknown');
      }
    } else {
      console.log("No authenticated user!");
      setArtistName('Unknown Artist');
      setArtistBirthYear('Unknown');
    }
  };

  const formatCategoryName = (name) => {
    return name.split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        handleInputChange("image", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleCategory = (categoryId) => {
    setFormData(prevData => ({
      ...prevData,
      categoryIds: prevData.categoryIds.includes(categoryId)
        ? prevData.categoryIds.filter(id => id !== categoryId)
        : [...prevData.categoryIds, categoryId]
    }));
  };

  const handleInputChange = (name, value) => {
    setFormData(prevData => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleNext = () => {
    if (!validateForm()) {
      return;
    }

    const selectedCategoryNames = categories
      .filter(cat => formData.categoryIds.includes(cat.id))
      .map(cat => cat.name);

    const artworkData = {
      ...formData,
      categoryNames: selectedCategoryNames,
      price: formData.isForSale ? formData.price : null,
      zipCode: formData.allowBuyerPickUp ? formData.zipCode : null,
      status: formData.isForSale ? 'available' : 'not for sale',
    };

    const navigationState = {
      artworkData,
      artistName,
      artistBirthYear,
      currentUserId,
      isEditMode,
    };

    // If we're in edit mode, add the necessary edit-related data
    if (location.state?.editMode) {
      navigationState.existingArtworkId = location.state.existingArtworkId;
      navigationState.originalImageUrl = location.state.originalImageUrl;
    }

    navigate('/listing-preview', { state: navigationState });
  };

  const validateForm = () => {
    if (!formData.title) {
      alert('Please enter a title for the artwork.');
      return false;
    }
    if (!formData.createdYear) {
      alert('Please enter the year the artwork was created.');
      return false;
    }
    if (formData.categoryIds.length === 0) {
      alert('Please select at least one category.');
      return false;
    }
    if (formData.isForSale && !formData.price) {
      alert('Please enter a price for the artwork.');
      return false;
    }
    if (!formData.materials) {
      alert('Please enter the materials used in the artwork.');
      return false;
    }
    if (!formData.size) {
      alert('Please enter the size of the artwork.');
      return false;
    }
    if (formData.allowBuyerPickUp && !formData.zipCode) {
      alert('Please enter a zip code for buyer pick-up.');
      return false;
    }
    if (!formData.image) {
      alert('Please upload an image of the artwork.');
      return false;
    }
    return true;
  };

  return (
    <Container>
      <ScrollView>
        <Header>Add Artwork</Header>
        <ImageUpload>
          {formData.image ? (
            <UploadedImage src={formData.image} alt="Artwork preview" />
          ) : (
            <>
              <ImageUploadText>*Files support .png, .jpg</ImageUploadText>
              <ImageUploadButton>
                Browse
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  style={{ display: 'none' }}
                />
              </ImageUploadButton>
            </>
          )}
        </ImageUpload>

        <Input
          placeholder="Title"
          value={formData.title}
          onChange={(e) => handleInputChange('title', e.target.value)}
        />

        <Input
          placeholder="Year Created"
          value={formData.createdYear}
          onChange={(e) => handleInputChange('createdYear', e.target.value)}
          type="number"
        />

        <SectionTitle>Categories</SectionTitle>
        {categories.map(category => (
          <CategoryCheckbox key={category.id} onClick={() => toggleCategory(category.id)}>
            <CheckboxLabel>
              {formData.categoryIds.includes(category.id) ? "☑" : "☐"} {category.name}
            </CheckboxLabel>
          </CategoryCheckbox>
        ))}

        <Spacer />

        <Input
          placeholder="$ Price"
          value={formData.price}
          onChange={(e) => handleInputChange('price', e.target.value)}
          type="number"
        />

        <Input
          placeholder="Materials"
          value={formData.materials}
          onChange={(e) => handleInputChange('materials', e.target.value)}
        />

        <Input
          placeholder="Size (e.g., 24 x 36 inches)"
          value={formData.size}
          onChange={(e) => handleInputChange('size', e.target.value)}
        />

        <TextArea
          placeholder="Description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
        />

        <CheckboxContainer>
          <CheckboxLabel onClick={() => handleInputChange('isPublicArt', !formData.isPublicArt)}>
            {formData.isPublicArt ? "☑" : "☐"} Public Art
          </CheckboxLabel>
          <CheckboxLabel onClick={() => handleInputChange('isForSale', !formData.isForSale)}>
            {formData.isForSale ? "☐" : "☑"} Not For Sale (Offers Only)
          </CheckboxLabel>
        </CheckboxContainer>

        <CheckboxLabel onClick={() => handleInputChange('allowBuyerPickUp', !formData.allowBuyerPickUp)}>
          {formData.allowBuyerPickUp ? "☑" : "☐"} Allow Buyer Pick-Up
        </CheckboxLabel>

        {formData.allowBuyerPickUp && (
          <Input
            placeholder="Pick-Up Location Zip Code"
            value={formData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
            type="number"
          />
        )}

        <SubmitButton onClick={handleNext}>
          Next
        </SubmitButton>
      </ScrollView>
    </Container>
  );
};

const Container = styled.div`
  flex: 1;
  background-color: white;
  min-height: 100vh;
`;

const ScrollView = styled.div`
  padding: 20px;
  padding-bottom: 40px;
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.h1`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 20px;
  text-align: center;
`;

const ImageUpload = styled.div`
  border: 1px dashed #ccc;
  border-radius: 5px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
  cursor: pointer;
`;

const ImageUploadText = styled.p`
  font-size: 12px;
  color: #888;
  margin-bottom: 10px;
`;

const ImageUploadButton = styled.label`
  font-size: 16px;
  color: white;
  background-color: #E91E63;
  padding: 10px 20px;
  border-radius: 5px;
  cursor: pointer;
`;

const UploadedImage = styled.img`
  width: 100%;
  max-height: 300px;
  object-fit: contain;
`;

const Input = styled.input`
  height: 40px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-bottom: 15px;
  padding: 0 10px;
  width: 100%;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  height: 100px;
  border: 1px solid #ccc;
  border-radius: 5px;
  margin-bottom: 15px;
  padding: 10px;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 10px;
`;

const CategoryCheckbox = styled.div`
  margin-bottom: 10px;
  cursor: pointer;
`;

const Spacer = styled.div`
  height: 20px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`;

const CheckboxLabel = styled.span`
  font-size: 16px;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  background-color: black;
  color: white;
  padding: 15px;
  border-radius: 5px;
  width: 100%;
  border: none;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  margin-top: 20px;

  &:hover {
    opacity: 0.9;
  }
`;

export default UploadArtScreen; 